// Intent: "Utwórz wspólne gospodarstwo?" (Stitch mock wsp_lne_gospodarstwo_
// zaproszenie) — the partner is added as a household member and the user SENDS
// the invite themselves via the system share sheet (WhatsApp/SMS/anything);
// nothing goes out without their tap. Honest copy: real cross-account data
// sync is a future backend step — today the invite installs the app for the
// partner (with the user's referral code when logged in).

import * as Clipboard from 'expo-clipboard';
import { router, useLocalSearchParams } from 'expo-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KeyboardAvoidingView, Platform, Pressable, Share, ScrollView, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';

import {
  createHouseholdApi,
  fetchHouseholdApi,
  fetchReferralInfoApi,
  type ReferralInfo,
} from '@/api/mobile-api';
import { AppText } from '@/components/ui/app-text';
import { Card } from '@/components/ui/card';
import { PrimaryButton } from '@/components/ui/primary-button';
import { ScreenHeader } from '@/components/ui/screen-header';
import { TextField } from '@/components/ui/text-field';
import { useAppMetaStore } from '@/store/app-meta-store';
import { useAuthStore } from '@/store/auth-store';
import { useHouseholdStore } from '@/store/household-store';
import { colors, radius, spacing } from '@/theme/tokens';

const APP_URL = 'https://opiekun-rodziny.expo.app';

export default function HouseholdInviteScreen() {
  const { t } = useTranslation();
  // from=onboarding -> finishing lands on the tabs, not back on onboarding.
  const { from } = useLocalSearchParams<{ from?: string }>();
  const account = useAuthStore((s) => s.account);
  const addMember = useHouseholdStore((s) => s.addMember);

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [info, setInfo] = useState<ReferralInfo | null>(null);
  const [hhCode, setHhCode] = useState<string | null>(null); // server household invite code
  const [sent, setSent] = useState(false);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const copyCode = async (key: string, code: string) => {
    await Clipboard.setStringAsync(code);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  useEffect(() => {
    if (!account) return;
    let cancelled = false;
    (async () => {
      const res = await fetchReferralInfoApi();
      if (!cancelled && res.ok && res.data) setInfo(res.data);
      // Ensure a server household exists so the invite carries a join code
      // (realny sync). Old backend (1.52) 404s -> graceful: no code, old flow.
      const created = await createHouseholdApi(name.trim() || 'Nasze gospodarstwo');
      if (!cancelled && created.ok && created.data) {
        setHhCode(created.data.inviteCode);
      } else if (!cancelled) {
        const existing = await fetchHouseholdApi(); // 409 already in one -> fetch it
        if (!cancelled && existing.ok && existing.data) setHhCode(existing.data.inviteCode);
      }
    })();
    return () => {
      cancelled = true;
    };
    // once per account on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account]);

  const leave = () => {
    if (from === 'onboarding') router.replace('/(tabs)');
    else router.back();
  };

  // Napper-style personal invite ("Ewelina zaprasza Cię do..."), ONE code only.
  // The referral talon is a separate topic with its own screen — mixing two
  // codes in one message confused invitees. The link carries the code so the
  // web app can prefill it (/dolacz?kod=...).
  const inviteText = () => {
    const senderName = account?.firstName ?? useAppMetaStore.getState().userName;
    const url = hhCode ? `${APP_URL}/dolacz?kod=${encodeURIComponent(hhCode)}` : APP_URL;
    if (!hhCode) return t('householdInvite.messageNoCode', { url });
    return senderName
      ? t('householdInvite.messageNamed', { name: senderName, hhCode, url })
      : t('householdInvite.messageAnon', { hhCode, url });
  };

  const onInvite = async () => {
    await addMember({ firstName: name, role: 'PARTNER', phone: phone || null });
    try {
      await Share.share({ message: inviteText() });
    } catch {
      // Desktop web without navigator.share — the member is added; the user can
      // still share the link from the Poleć aplikację screen.
    }
    setSent(true);
  };

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }} edges={['top', 'bottom']}>
      <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <ScrollView
          keyboardShouldPersistTaps="handled"
          contentContainerStyle={{ padding: spacing.screen, gap: spacing.md, paddingBottom: spacing.xl }}
        >
          <ScreenHeader title={t('householdInvite.header')} />

          {/* Hero illustration substitute: icon tile (no AI art in-app) */}
          <View
            style={{
              alignItems: 'center',
              gap: spacing.sm,
              backgroundColor: colors.infoBg,
              borderRadius: radius.card,
              padding: spacing.lg,
            }}
          >
            <Ionicons name="people-circle-outline" size={56} color={colors.primary} />
            <AppText variant="headlineMd" color={colors.navy} style={{ textAlign: 'center' }}>
              {t('householdInvite.title')}
            </AppText>
            <AppText variant="bodyLg" style={{ textAlign: 'center' }}>
              {t('householdInvite.subtitle')}
            </AppText>
          </View>

          {/* Codes are always visible + copyable (owner request: they only showed up
              later, inside the share message). hhCode loads on mount once created. */}
          {(hhCode || info) && (
            <Card style={{ gap: spacing.sm }}>
              <AppText variant="labelMd" style={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                {t('householdInvite.codesTitle')}
              </AppText>
              {hhCode && (
                <Pressable
                  accessibilityRole="button"
                  onPress={() => void copyCode('hh', hhCode)}
                  style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}
                >
                  <View style={{ flex: 1 }}>
                    <AppText variant="labelMd" color={colors.outline}>
                      {t('householdInvite.hhCodeLabel')}
                    </AppText>
                    <AppText variant="headlineSm" color={colors.primary}>
                      {hhCode}
                    </AppText>
                  </View>
                  <Ionicons name={copiedKey === 'hh' ? 'checkmark-outline' : 'copy-outline'} size={22} color={colors.primary} />
                </Pressable>
              )}
              {info && (
                <Pressable
                  accessibilityRole="button"
                  onPress={() => void copyCode('ref', info.code)}
                  style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}
                >
                  <View style={{ flex: 1 }}>
                    <AppText variant="labelMd" color={colors.outline}>
                      {t('householdInvite.referralCodeLabel')}
                    </AppText>
                    <AppText variant="headlineSm" color={colors.primary}>
                      {info.code}
                    </AppText>
                  </View>
                  <Ionicons name={copiedKey === 'ref' ? 'checkmark-outline' : 'copy-outline'} size={22} color={colors.primary} />
                </Pressable>
              )}
            </Card>
          )}

          {sent ? (
            <>
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: spacing.sm,
                  backgroundColor: colors.successBg,
                  borderRadius: radius.card,
                  padding: spacing.md,
                }}
              >
                <Ionicons name="checkmark-circle-outline" size={24} color={colors.navy} />
                <AppText variant="bodyLg" color={colors.navy} style={{ flex: 1 }}>
                  {t('householdInvite.sentInfo', { name: name.trim() })}
                </AppText>
              </View>
              <PrimaryButton label={t('householdInvite.done')} onPress={leave} />
            </>
          ) : (
            <>
              <TextField
                label={t('householdInvite.nameLabel')}
                placeholder={t('householdInvite.namePlaceholder')}
                value={name}
                onChangeText={setName}
              />
              <TextField
                label={t('householdInvite.phoneLabel')}
                placeholder="+48 000 000 000"
                keyboardType="phone-pad"
                value={phone}
                onChangeText={setPhone}
              />

              <PrimaryButton label={t('householdInvite.cta')} disabled={!name.trim()} onPress={onInvite} />

              <Pressable
                accessibilityRole="button"
                onPress={leave}
                style={{ minHeight: 44, alignItems: 'center', justifyContent: 'center' }}
              >
                <AppText variant="labelLg" color={colors.primary}>
                  {t('householdInvite.later')}
                </AppText>
              </Pressable>

              <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: spacing.xs }}>
                <Ionicons name="information-circle-outline" size={16} color={colors.outline} style={{ marginTop: 2 }} />
                <AppText variant="labelMd" color={colors.outline} style={{ flex: 1 }}>
                  {t('householdInvite.note')}
                </AppText>
              </View>
            </>
          )}
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}
